import _ from '@luna/lodash';

interface Options {
    emptyArrays?: boolean;
    emptyObjects?: boolean;
    emptyStrings?: boolean;
    nullValues?: boolean;
    undefinedValues?: boolean;
    whiteListAttributes?: string[];
}

export function deepCleanOptional<T>(
    object: Record<string, T>,
): Record<string, Exclude<T, undefined | null>> {
    return deepClean(object, {
        emptyArrays: false,
        emptyObjects: false,
        emptyStrings: false,
        nullValues: false,
        undefinedValues: true,
    });
}

/**
 * Deeply cleans an object by removing specified empty or null values.
 *
 * This function recursively traverses the object and removes key-value pairs based on the specified options.
 *
 * @param {Record<string, any>} object - The object to clean
 * @param {Options} options - Configuration options for cleaning
 * @param {boolean} [options.emptyArrays=true] - Whether to remove empty arrays
 * @param {boolean} [options.emptyObjects=true] - Whether to remove empty objects
 * @param {boolean} [options.emptyStrings=true] - Whether to remove empty strings
 * @param {boolean} [options.nullValues=true] - Whether to remove null values
 * @param {boolean} [options.undefinedValues=true] - Whether to remove undefined values
 * @param {string[]} [options.whiteListAttributes=[]] - Array of keys to always keep, regardless of their values
 * @returns {Record<string, any>} A new object with specified values removed
 *
 * @example
 * const input = {
 *   a: 1,
 *   b: '',
 *   c: null,
 *   d: undefined,
 *   e: [],
 *   f: {},
 *   g: { h: null }
 * };
 * const cleaned = deepClean(input, {
 *   emptyStrings: true,
 *   nullValues: true,
 *   undefinedValues: true,
 *   emptyArrays: false,
 *   emptyObjects: false
 * });
 * // Result: { a: 1, e: [], f: {} }
 *
 * @example
 * // Using whiteListAttributes
 * const input = {
 *   a: null,
 *   b: undefined,
 *   c: ''
 * };
 * const cleaned = deepClean(input, {
 *   whiteListAttributes: ['b']
 * });
 * // Result: { b: undefined }
 */
export function deepClean(object: Record<string, any>, options: Options = {}): Record<string, any> {
    const {
        emptyArrays = true,
        emptyObjects = true,
        emptyStrings = true,
        nullValues = true,
        undefinedValues = true,
        whiteListAttributes = [],
    } = options;

    return _.transform(object, (result, value, key) => {
        if (key && whiteListAttributes.includes(key)) {
            result[key as string] = value;
            return;
        }

        if (Array.isArray(value) || _.isPlainObject(value)) {
            // eslint-disable-next-line no-param-reassign
            value = deepClean(value, options);
        }

        if (emptyObjects && _.isPlainObject(value) && _.isEmpty(value)) {
            return;
        }

        if (emptyArrays && Array.isArray(value) && !value.length) {
            return;
        }

        if (emptyStrings && value === '') {
            return;
        }

        if (nullValues && value === null) {
            return;
        }

        if (undefinedValues && value === undefined) {
            return;
        }

        if (Array.isArray(result)) {
            result.push(value);
            return;
        }

        result[key as string] = value;
    });
}
